/*
 * Legacy TriStateCheckbox component - Copy from PrimeNG 17
 * Created for compatibility since TriStateCheckbox was removed in PrimeNG 18
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  booleanAttribute,
  forwardRef,
  numberAttribute
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TriStateCheckboxChangeEvent } from './tristatecheckbox.interface';

export const TRISTATECHECKBOX_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LegacyTriStateCheckboxComponent),
  multi: true
};

/**
 * Legacy TriStateCheckbox - used to select either 'true', 'false' or 'null' as the value.
 * @group Components
 */
@Component({
  selector: 'p-triStateCheckbox',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      [ngStyle]="style"
      [ngClass]="{ 'p-checkbox p-component': true, 'p-checkbox-disabled': disabled, 'p-checkbox-focused': focused }"
      [class]="styleClass"
      (click)="onClick($event, input)"
    >
      <div class="p-hidden-accessible">
        <input
          #input
          [attr.id]="inputId"
          type="checkbox"
          [name]="name"
          [attr.tabindex]="tabindex"
          [readonly]="readonly"
          [disabled]="disabled"
          (keydown)="onKeyDown($event)"
          (focus)="onFocus()"
          (blur)="onBlur()"
          [attr.aria-labelledby]="ariaLabelledBy"
          [attr.aria-label]="ariaLabel"
          inputmode="none"
        />
      </div>
      <div class="p-checkbox-box" role="checkbox" [attr.aria-checked]="value === true" [ngClass]="{ 'p-highlight': value != null, 'p-disabled': disabled, 'p-focus': focused }">
        <ng-container *ngIf="value === true">
          <span *ngIf="checkboxTrueIcon" [ngClass]="checkboxTrueIcon" class="p-checkbox-icon"></span>
          <ng-container *ngIf="!checkboxTrueIcon">
            <span class="p-checkbox-icon pi pi-check"></span>
          </ng-container>
        </ng-container>
        <ng-container *ngIf="value === false">
          <span *ngIf="checkboxFalseIcon" [ngClass]="checkboxFalseIcon" class="p-checkbox-icon"></span>
          <ng-container *ngIf="!checkboxFalseIcon">
            <span class="p-checkbox-icon pi pi-times"></span>
          </ng-container>
        </ng-container>
      </div>
    </div>
    <label
      class="p-checkbox-label"
      (click)="onClick($event, input)"
      [ngClass]="{ 'p-checkbox-label-active': value != null, 'p-disabled': disabled, 'p-checkbox-label-focus': focused }"
      *ngIf="label"
      [attr.for]="inputId"
    >{{ label }}</label>
  `,
  styles: [`
    .p-checkbox {
      display: inline-flex;
      cursor: pointer;
      user-select: none;
      vertical-align: bottom;
      position: relative;
    }

    .p-checkbox-disabled {
      cursor: default !important;
      pointer-events: none;
    }

    .p-checkbox-box {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    p-triStateCheckbox {
      display: inline-flex;
      vertical-align: bottom;
      align-items: center;
    }

    .p-checkbox-label {
      margin-left: 0.5rem;
    }

    .p-hidden-accessible {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }
  `],
  providers: [TRISTATECHECKBOX_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'p-element'
  }
})
export class LegacyTriStateCheckboxComponent implements ControlValueAccessor {
  constructor(private cd: ChangeDetectorRef) {}

  /**
   * When present, it specifies that the element should be disabled.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) disabled: boolean | undefined;
  /**
   * Name of the component.
   * @group Props
   */
  @Input() name: string | undefined;
  /**
   * Defines a string that labels the input for accessibility.
   * @group Props
   */
  @Input() ariaLabel: string | undefined;
  /**
   * Establishes relationships between the component and label(s) where its value should be one or more element IDs.
   * @group Props
   */
  @Input() ariaLabelledBy: string | undefined;
  /**
   * Index of the element in tabbing order.
   * @group Props
   */
  @Input({ transform: numberAttribute }) tabindex: number | undefined;
  /**
   * Identifier of the focus input to match a label defined for the component.
   * @group Props
   */
  @Input() inputId: string | undefined;
  /**
   * Inline style of the component.
   * @group Props
   */
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Style class of the component.
   * @group Props
   */
  @Input() styleClass: string | undefined;
  /**
   * Label of the checkbox.
   * @group Props
   */
  @Input() label: string | undefined;
  /**
   * When present, it specifies that the component cannot be edited.
   * @group Props
   */
  @Input({ transform: booleanAttribute }) readonly: boolean | undefined;
  /**
   * Specifies the icon for checkbox true value.
   * @group Props
   */
  @Input() checkboxTrueIcon: string | undefined;
  /**
   * Specifies the icon for checkbox false value.
   * @group Props
   */
  @Input() checkboxFalseIcon: string | undefined;
  /**
   * Callback to invoke on value change.
   * @group Emits
   */
  @Output() onChange: EventEmitter<TriStateCheckboxChangeEvent> = new EventEmitter<TriStateCheckboxChangeEvent>();

  focused: boolean | null = null;

  value: boolean | null = null;

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  onClick(event: Event, input: HTMLInputElement) {
    if (!this.disabled && !this.readonly) {
      this.toggle(event);
      this.focused = true;
      input.focus();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.toggle(event);
      event.preventDefault();
    }
  }

  toggle(event: Event) {
    if (this.value == null || this.value == undefined) {
      this.value = true;
    } else if (this.value == true) {
      this.value = false;
    } else if (this.value == false) {
      this.value = null;
    }

    this.onModelChange(this.value);
    this.onChange.emit({
      originalEvent: event,
      value: this.value
    });
  }

  onFocus() {
    this.focused = true;
  }

  onBlur() {
    this.focused = false;
    this.onModelTouched();
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cd.markForCheck();
  }
}
